// src/services/activityService.ts
import { supabase } from "../lib/supabase";

// Activity types - expanded to include tasks
// PKT-0049 Phase 1 added 11 new types covering channel-aware messaging
// (sent_text · received_text · sent_dm · received_dm) + email engagement
// (email_opened · email_clicked) + bot orchestration (bot_drafted ·
// bot_escalated) + shared-list lifecycle (list_shared · list_opened ·
// interest_signal). Backend write paths for these land in PKT-0049b
// (architecture decision) + Phase 2 (PR #104 bot_flow_runner hooks) +
// Phase 3 (PKT-0040 SMS inbound) + Phase 4 (PKT-0036 Instagram).
export type ActivityType =
  | "client_created"
  | "client_updated"
  | "client_deleted"
  | "search_executed"
  | "search_saved"
  | "email_sent"
  | "guest_card_sent"
  | "guest_card_viewed"
  | "property_viewed"
  | "listing_shared"
  | "note_added"
  | "call_logged"
  | "meeting_scheduled"
  | "task_created"
  | "task_completed"
  | "tour_scheduled"
  | "tour_completed"
  | "invoice_sent"
  | "payment_received"
  // PKT-0049 Phase 1 expansion (Director #6 client-story-redesign §E.2)
  | "sent_text"
  | "received_text"
  | "sent_dm"
  | "received_dm"
  | "email_opened"
  | "email_clicked"
  | "bot_drafted"
  | "bot_escalated"
  | "list_shared"
  | "list_opened"
  | "interest_signal";

export type EntityType =
  | "client"
  | "property"
  | "search"
  | "email"
  | "guest_card"
  | "task";

// Task types for the system
export type TaskType =
  | "scheduled_tour"
  | "touring"
  | "follow_up"
  | "invoice_sent"
  | "payment_received"
  | "application_submitted"
  | "lease_signed"
  | "move_in"
  | "custom";

export interface Activity {
  id: string;
  user_id: string;
  activity_type: ActivityType;
  entity_type: EntityType;
  entity_id?: string;
  metadata: Record<string, any>;
  created_at: string;
}

export interface ClientTask {
  id: string;
  client_id: string;
  task_type: TaskType;
  title: string;
  description?: string;
  status: "pending" | "completed" | "cancelled";
  due_date?: string;
  completed_at?: string;
  created_at: string;
  created_by: string;
}

export interface ClientNote {
  id: string;
  client_id: string;
  content: string;
  created_at: string;
  created_by: string;
}

class ActivityService {
  private userId: string | null = null;

  setUserId(userId: string) {
    this.userId = userId;
  }

  getUserId(): string | null {
    return this.userId;
  }

  async track(
    activityType: ActivityType,
    entityType: EntityType,
    entityId?: string,
    metadata: Record<string, any> = {}
  ): Promise<Activity | null> {
    if (!this.userId) {
      // Try to get user from supabase
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        this.userId = user.id;
      } else {
        console.warn(
          "ActivityService: No user ID set, skipping activity tracking"
        );
        return null;
      }
    }

    try {
      const { data, error } = await supabase
        .from("activities")
        .insert({
          user_id: this.userId,
          activity_type: activityType,
          entity_type: entityType,
          entity_id: entityId,
          metadata: { ...metadata, timestamp: new Date().toISOString() },
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error("ActivityService: Failed to track activity", error);
      return null;
    }
  }

  // ========== Client Activity Methods ==========

  async trackClientCreated(clientId: string, clientName: string) {
    return this.track("client_created", "client", clientId, {
      client_name: clientName,
    });
  }

  async trackClientUpdated(
    clientId: string,
    clientName: string,
    changes: Record<string, any>
  ) {
    return this.track("client_updated", "client", clientId, {
      client_name: clientName,
      changes,
    });
  }

  // ========== Search Methods ==========

  async trackSearchExecuted(
    criteria: Record<string, any>,
    resultCount: number
  ) {
    return this.track("search_executed", "search", undefined, {
      criteria,
      result_count: resultCount,
    });
  }

  async trackSearchSaved(
    searchId: string,
    searchName: string,
    clientId: string
  ) {
    return this.track("search_saved", "search", searchId, {
      search_name: searchName,
      client_id: clientId,
    });
  }

  // Dead communication wrappers (trackEmailSent, trackGuestCardSent, trackCallLogged,
  // trackMeetingScheduled, trackListingShared) were removed 2026-08-01: zero callers, and each
  // would have written a contact-type activity_type that fires migration 031's
  // last_contacted_at trigger if ever wired up without a confirm gate. Confirmed contact-type
  // writes go through the audited call sites listed in migration 031's header.

  // ========== Property Methods ==========

  async trackPropertyViewed(propertyId: string, propertyName: string) {
    return this.track("property_viewed", "property", propertyId, {
      property_name: propertyName,
    });
  }

  // ========== Note Methods ==========

  async addNote(
    clientId: string,
    clientName: string,
    content: string
  ): Promise<ClientNote | null> {
    if (!this.userId) {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) this.userId = user.id;
      else return null;
    }

    try {
      // Insert note into client_notes table
      const { data: note, error: noteError } = await supabase
        .from("client_notes")
        .insert({
          client_id: clientId,
          content: content,
          created_by: this.userId,
        })
        .select()
        .single();

      if (noteError) throw noteError;

      // Track activity
      await this.track("note_added", "client", clientId, {
        client_name: clientName,
        note_preview: content.substring(0, 100),
        note_id: note.id,
      });

      return note;
    } catch (error) {
      console.error("ActivityService: Failed to add note", error);
      return null;
    }
  }

  async getClientNotes(clientId: string): Promise<ClientNote[]> {
    try {
      const { data, error } = await supabase
        .from("client_notes")
        .select("*")
        .eq("client_id", clientId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error("ActivityService: Failed to fetch notes", error);
      return [];
    }
  }

  async deleteNote(noteId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from("client_notes")
        .delete()
        .eq("id", noteId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error("ActivityService: Failed to delete note", error);
      return false;
    }
  }

  // ========== Task Methods ==========

  async createTask(
    clientId: string,
    clientName: string,
    taskType: TaskType,
    title: string,
    description?: string,
    dueDate?: string
  ): Promise<ClientTask | null> {
    if (!this.userId) {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) this.userId = user.id;
      else return null;
    }

    try {
      const { data: task, error: taskError } = await supabase
        .from("client_tasks")
        .insert({
          client_id: clientId,
          task_type: taskType,
          title: title,
          description: description,
          status: "pending",
          due_date: dueDate,
          created_by: this.userId,
        })
        .select()
        .single();

      if (taskError) throw taskError;

      // Track activity based on task type
      const activityType = this.getActivityTypeForTask(taskType);
      await this.track(activityType, "client", clientId, {
        client_name: clientName,
        task_type: taskType,
        task_title: title,
        task_id: task.id,
        due_date: dueDate,
      });

      return task;
    } catch (error) {
      console.error("ActivityService: Failed to create task", error);
      return null;
    }
  }

  async completeTask(
    taskId: string,
    clientId: string,
    clientName: string
  ): Promise<boolean> {
    try {
      const { data: task, error } = await supabase
        .from("client_tasks")
        .update({
          status: "completed",
          completed_at: new Date().toISOString(),
        })
        .eq("id", taskId)
        .select()
        .single();

      if (error) throw error;

      // Track completion
      await this.track("task_completed", "client", clientId, {
        client_name: clientName,
        task_type: task.task_type,
        task_title: task.title,
        task_id: taskId,
      });

      return true;
    } catch (error) {
      console.error("ActivityService: Failed to complete task", error);
      return false;
    }
  }

  async getClientTasks(
    clientId: string,
    includeCompleted: boolean = false
  ): Promise<ClientTask[]> {
    try {
      let query = supabase
        .from("client_tasks")
        .select("*")
        .eq("client_id", clientId)
        .order("created_at", { ascending: false });

      if (!includeCompleted) {
        query = query.eq("status", "pending");
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error("ActivityService: Failed to fetch tasks", error);
      return [];
    }
  }

  async getMostRecentTask(clientId: string): Promise<ClientTask | null> {
    try {
      const { data, error } = await supabase
        .from("client_tasks")
        .select("*")
        .eq("client_id", clientId)
        .order("created_at", { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== "PGRST116") throw error; // PGRST116 = no rows
      return data || null;
    } catch (error) {
      console.error("ActivityService: Failed to fetch recent task", error);
      return null;
    }
  }

  private getActivityTypeForTask(taskType: TaskType): ActivityType {
    switch (taskType) {
      case "scheduled_tour":
      case "touring":
        return "tour_scheduled";
      case "invoice_sent":
        return "invoice_sent";
      case "payment_received":
        return "payment_received";
      default:
        return "task_created";
    }
  }

  // ========== Last Contact Methods ==========

  async getLastContactDate(clientId: string): Promise<string | null> {
    try {
      const contactTypes: ActivityType[] = [
        "call_logged",
        "email_sent",
        "meeting_scheduled",
        "listing_shared",
        "guest_card_sent",
      ];

      const { data, error } = await supabase
        .from("activities")
        .select("created_at")
        .eq("entity_id", clientId)
        .in("activity_type", contactTypes)
        .order("created_at", { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== "PGRST116") throw error;
      return data?.created_at || null;
    } catch (error) {
      console.error("ActivityService: Failed to get last contact date", error);
      return null;
    }
  }

  // PKT-0411 (N2): the updateClientLastContacted helper was DELETED here. It had zero
  // callers across frontend/src (verified) and wrote clients.last_contacted_at
  // from the CLIENT's clock, which is the one-directional skew described in
  // LogActivityDialog's handleSubmit — a fast device writes a future timestamp
  // and migration 031's forward-only trigger can never correct it.
  //
  // It is deleted rather than left dormant because an invitingly-named helper is
  // how this defect gets reintroduced: `clients.last_contacted_at` is owned by
  // trg_bump_client_last_contacted_on_activity, and application code should
  // advance it ONLY by inserting a contact-type activity for a CONFIRMED action.
  // See live/ops/audits/CONTACT-TRUTH-WRITE-SITE-AUDIT-2026-07-30.md.

  // ========== Fetch Activities ==========

  async getRecentActivities(limit: number = 20): Promise<Activity[]> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from("activities")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error("ActivityService: Failed to fetch activities", error);
      return [];
    }
  }

  async getActivitiesForEntity(
    entityType: EntityType,
    entityId: string
  ): Promise<Activity[]> {
    try {
      const { data, error } = await supabase
        .from("activities")
        .select("*")
        .eq("entity_type", entityType)
        .eq("entity_id", entityId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error(
        "ActivityService: Failed to fetch entity activities",
        error
      );
      return [];
    }
  }

  async getActivitiesForClient(
    clientId: string,
    limit: number = 50
  ): Promise<Activity[]> {
    try {
      const { data, error } = await supabase
        .from("activities")
        .select("*")
        .eq("entity_id", clientId)
        .order("created_at", { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error(
        "ActivityService: Failed to fetch client activities",
        error
      );
      return [];
    }
  }

  // ========== Search/Filter Activities ==========

  async searchActivities(params: {
    startDate?: string;
    endDate?: string;
    activityTypes?: ActivityType[];
    clientId?: string;
    searchQuery?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ activities: Activity[]; total: number }> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return { activities: [], total: 0 };

      let query = supabase
        .from("activities")
        .select("*", { count: "exact" })
        .eq("user_id", user.id);

      if (params.startDate) {
        query = query.gte("created_at", params.startDate);
      }
      if (params.endDate) {
        query = query.lte("created_at", params.endDate);
      }
      if (params.activityTypes && params.activityTypes.length > 0) {
        query = query.in("activity_type", params.activityTypes);
      }
      if (params.clientId) {
        query = query.eq("entity_id", params.clientId);
      }

      query = query
        .order("created_at", { ascending: false })
        .range(
          params.offset || 0,
          (params.offset || 0) + (params.limit || 50) - 1
        );

      const { data, error, count } = await query;

      if (error) throw error;

      let activities = data || [];

      // Client-side search filter if provided
      if (params.searchQuery) {
        const q = params.searchQuery.toLowerCase();
        activities = activities.filter((a) => {
          const desc = formatActivityDescription(a).toLowerCase();
          const clientName = (a.metadata?.client_name || "").toLowerCase();
          return desc.includes(q) || clientName.includes(q);
        });
      }

      return { activities, total: count || 0 };
    } catch (error) {
      console.error("ActivityService: Failed to search activities", error);
      return { activities: [], total: 0 };
    }
  }

  // ========== Report Generation ==========

  async generateActivityReport(params: {
    startDate: string;
    endDate: string;
    groupBy?: "day" | "week" | "month";
  }): Promise<{
    summary: {
      totalActivities: number;
      byType: Record<string, number>;
      byClient: { clientId: string; clientName: string; count: number }[];
    };
    activities: Activity[];
  }> {
    try {
      const { activities } = await this.searchActivities({
        startDate: params.startDate,
        endDate: params.endDate,
        limit: 10000,
      });

      // Calculate summary
      const byType: Record<string, number> = {};
      const clientCounts: Record<
        string,
        { clientName: string; count: number }
      > = {};

      activities.forEach((a) => {
        // Count by type
        byType[a.activity_type] = (byType[a.activity_type] || 0) + 1;

        // Count by client
        if (a.entity_id && a.metadata?.client_name) {
          if (!clientCounts[a.entity_id]) {
            clientCounts[a.entity_id] = {
              clientName: a.metadata.client_name,
              count: 0,
            };
          }
          clientCounts[a.entity_id].count++;
        }
      });

      const byClient = Object.entries(clientCounts)
        .map(([clientId, data]) => ({ clientId, ...data }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 20);

      return {
        summary: {
          totalActivities: activities.length,
          byType,
          byClient,
        },
        activities,
      };
    } catch (error) {
      console.error("ActivityService: Failed to generate report", error);
      return {
        summary: { totalActivities: 0, byType: {}, byClient: [] },
        activities: [],
      };
    }
  }
}

// Singleton instance
export const activityService = new ActivityService();

// ========== Activity display helpers ==========

export const getActivityIcon = (type: ActivityType): string => {
  const icons: Record<ActivityType, string> = {
    client_created: "👤",
    client_updated: "✏️",
    client_deleted: "🗑️",
    search_executed: "🔍",
    search_saved: "💾",
    email_sent: "📧",
    guest_card_sent: "📋",
    guest_card_viewed: "👁️",
    property_viewed: "🏠",
    listing_shared: "📤",
    note_added: "📝",
    call_logged: "📞",
    meeting_scheduled: "📅",
    task_created: "✅",
    task_completed: "🎉",
    tour_scheduled: "🚶",
    tour_completed: "🏁",
    invoice_sent: "💵",
    payment_received: "💰",
    // PKT-0049 Phase 1 expansion
    sent_text: "📱",
    received_text: "📩",
    sent_dm: "💬",
    received_dm: "💬",
    email_opened: "👁",
    email_clicked: "🔗",
    bot_drafted: "🤖",
    bot_escalated: "⚠️",
    list_shared: "📤",
    list_opened: "📬",
    interest_signal: "⭐",
  };
  return icons[type] || "📌";
};

export const getActivityLabel = (type: ActivityType): string => {
  const labels: Record<ActivityType, string> = {
    client_created: "Created client",
    client_updated: "Updated client",
    client_deleted: "Deleted client",
    search_executed: "Executed search",
    search_saved: "Saved search",
    email_sent: "Sent email",
    guest_card_sent: "Sent guest card",
    guest_card_viewed: "Guest card viewed",
    property_viewed: "Viewed property",
    listing_shared: "Shared listings",
    note_added: "Added note",
    call_logged: "Logged call",
    meeting_scheduled: "Scheduled meeting",
    task_created: "Created task",
    task_completed: "Completed task",
    tour_scheduled: "Scheduled tour",
    tour_completed: "Completed tour",
    invoice_sent: "Sent invoice",
    payment_received: "Payment received",
    // PKT-0049 Phase 1 expansion
    sent_text: "Sent text",
    received_text: "Received text",
    sent_dm: "Sent DM",
    received_dm: "Received DM",
    email_opened: "Opened email",
    email_clicked: "Clicked email link",
    bot_drafted: "Bot drafted reply",
    bot_escalated: "Bot escalated",
    list_shared: "Shared list",
    list_opened: "List opened",
    interest_signal: "Marked interested",
  };
  return labels[type] || type;
};

export const getActivityColor = (type: ActivityType): string => {
  const colors: Record<ActivityType, string> = {
    client_created: "#4CAF50",
    client_updated: "#2196F3",
    client_deleted: "#f44336",
    search_executed: "#9C27B0",
    search_saved: "#673AB7",
    email_sent: "#00BCD4",
    guest_card_sent: "#FF9800",
    guest_card_viewed: "#8BC34A",
    property_viewed: "#607D8B",
    listing_shared: "#3F51B5",
    note_added: "#795548",
    call_logged: "#009688",
    meeting_scheduled: "#E91E63",
    task_created: "#FF5722",
    task_completed: "#4CAF50",
    tour_scheduled: "#00BCD4",
    tour_completed: "#8BC34A",
    invoice_sent: "#FFC107",
    payment_received: "#4CAF50",
    // PKT-0049 Phase 1 expansion
    sent_text: "#00BCD4",
    received_text: "#009688",
    sent_dm: "#00BCD4",
    received_dm: "#009688",
    email_opened: "#66BB6A",
    email_clicked: "#43A047",
    bot_drafted: "#7E57C2",
    bot_escalated: "#FF7043",
    list_shared: "#3F51B5",
    list_opened: "#66BB6A",
    interest_signal: "#FF6F00",
  };
  return colors[type] || "#757575";
};

export const formatActivityDescription = (activity: Activity): string => {
  const { activity_type, metadata } = activity;

  switch (activity_type) {
    case "client_created":
      return `Created new client: ${metadata.client_name}`;
    case "client_updated":
      return `Updated client: ${metadata.client_name}`;
    case "search_executed":
      return `Searched ${metadata.result_count} properties`;
    case "search_saved":
      return `Saved search "${metadata.search_name}"`;
    case "email_sent":
      return `Sent email to ${metadata.client_name}${
        metadata.subject ? `: "${metadata.subject}"` : ""
      }`;
    case "guest_card_sent":
      return `Sent guest card for ${metadata.property_name} to ${metadata.client_name}`;
    case "property_viewed":
      return `Viewed property: ${metadata.property_name}`;
    case "listing_shared":
      return `Shared ${metadata.unit_count} listings with ${metadata.client_name}`;
    case "call_logged":
      return `Logged call with ${metadata.client_name}${
        metadata.outcome ? ` - ${metadata.outcome}` : ""
      }`;
    case "meeting_scheduled":
      return `Scheduled ${metadata.meeting_type || "meeting"} with ${
        metadata.client_name
      }`;
    case "note_added":
      return `Added note for ${
        metadata.client_name
      }: "${metadata.note_preview?.substring(0, 50)}..."`;
    case "task_created":
      return `Created task "${metadata.task_title}" for ${metadata.client_name}`;
    case "task_completed":
      return `Completed task "${metadata.task_title}" for ${metadata.client_name}`;
    case "tour_scheduled":
      return `Scheduled tour for ${metadata.client_name}`;
    case "tour_completed":
      return `Completed tour with ${metadata.client_name}`;
    case "invoice_sent":
      return `Sent invoice to ${metadata.client_name}`;
    case "payment_received":
      return `Received payment from ${metadata.client_name}`;
    // PKT-0049 Phase 1 expansion — metadata-tolerant descriptions for the new types
    case "sent_text":
      return `Sent text to ${metadata.client_name || "client"}${
        metadata.body_preview
          ? `: "${String(metadata.body_preview).substring(0, 60)}"`
          : ""
      }`;
    case "received_text":
      return `Text from ${metadata.client_name || "client"}${
        metadata.body_preview
          ? `: "${String(metadata.body_preview).substring(0, 60)}"`
          : ""
      }`;
    case "sent_dm":
      return `Sent DM to ${
        metadata.client_name || metadata.sender_handle || "client"
      }`;
    case "received_dm":
      return `DM from ${
        metadata.client_name || metadata.sender_handle || "client"
      }`;
    case "email_opened":
      return `${metadata.client_name || "Client"} opened email${
        metadata.subject ? ` "${metadata.subject}"` : ""
      }`;
    case "email_clicked":
      return `${metadata.client_name || "Client"} clicked email link${
        metadata.subject ? ` in "${metadata.subject}"` : ""
      }`;
    case "bot_drafted":
      return `Bot drafted reply${
        metadata.template_type ? ` (${metadata.template_type})` : ""
      }${metadata.channel ? ` on ${metadata.channel}` : ""}`;
    case "bot_escalated":
      return `Bot escalated${
        metadata.escalation_reason ? ` — ${metadata.escalation_reason}` : ""
      }`;
    case "list_shared":
      return `Shared list with ${metadata.client_name || "client"}${
        metadata.property_count ? ` (${metadata.property_count} properties)` : ""
      }`;
    case "list_opened":
      return `${metadata.client_name || "Client"} opened the shared list`;
    case "interest_signal":
      return `${metadata.client_name || "Client"} marked interested${
        metadata.property_name ? ` in ${metadata.property_name}` : ""
      }`;
    default:
      return getActivityLabel(activity_type);
  }
};

export const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

// Task type display helpers
export const getTaskTypeLabel = (type: TaskType): string => {
  const labels: Record<TaskType, string> = {
    scheduled_tour: "Scheduled Tour",
    touring: "Touring",
    follow_up: "Follow Up",
    invoice_sent: "Invoice Sent",
    payment_received: "Payment Received",
    application_submitted: "Application Submitted",
    lease_signed: "Lease Signed",
    move_in: "Move In",
    custom: "Custom Task",
  };
  return labels[type] || type;
};

export const getTaskTypeColor = (type: TaskType): string => {
  const colors: Record<TaskType, string> = {
    scheduled_tour: "#00BCD4",
    touring: "#2196F3",
    follow_up: "#FF9800",
    invoice_sent: "#FFC107",
    payment_received: "#4CAF50",
    application_submitted: "#9C27B0",
    lease_signed: "#8BC34A",
    move_in: "#4CAF50",
    custom: "#607D8B",
  };
  return colors[type] || "#757575";
};
